import React, { useEffect, useState } from 'react';
import { Button, Form, Row } from 'react-bootstrap';
import { GetReportAttendanceGame, GetReportAttendanceSeason, GetPlayers } from '../../Services/ApiSvc';
import { columnManager } from '../Common/ColumnManager';
import ExcelExporter from '../Common/ExcelExporter';
import PDFExporter from '../Common/PDFExporter';
import StatFilter, { addFilterAsParameter, convertFilterToNote, createFilter } from '../Common/StatFilter';
import TabulatorTable from '../Common/TabulatorTable';

function AttendanceReport() {
    let curSeason = new Date().getMonth() < 3 ? new Date().getFullYear() - 1 : new Date().getFullYear();
    
    const [seasonColumns, setSeasonColumns] = useState(null);
    const [gameColumns, setGameColumns] = useState(null);
    const [seasonData, setSeasonData] = useState(null);
    const [gameData, setGameData] = useState(null);
    const [excelNotes, setExcelNotes] = useState([]);
    const [filters, setFilters] = useState([]);
    const [season, setSeason] = useState(curSeason.toString());

    useEffect(() => {
        (async function Initialize() {
            let allPlayersFromAPI = (await GetPlayers()).filter(player => !player.transactionOnly);
            let colGroups = await columnManager.getAllGroupsBySection('AttendanceReport');
            
            let seasonColumns = colGroups.find(group => group.label === 'AttendanceReportSeason').columns;
            let gameColumns = colGroups.find(group => group.label === 'AttendanceReportGame').columns;

            let gameColumnsWithResponsive = gameColumns.map(col => {
                if ([882, 883, 884, 885, 886, 887, 888, 889, 890, 891, 892, 893, 894, 895, 896, 897, 898, 899].includes(col.id)) {
                    col.responsive = 2;
                } else {
                    col.responsive = 0;
                }
                return col;
            });

            let attendanceFilters = [];
            colGroups.find(group => group.label === 'AttendanceFilters').columns
                .forEach((column, filterIndex) => {
                    let filter = Object.assign({}, createFilter(filterIndex, column));
                    switch (column.id) {
                        case 395:
                            filter.people = allPlayersFromAPI;
                            break;
                        case 599:
                            filter.value = "1,2,3,4,5,6,7";
                            break;
                        case 600:
                            filter.value = "0,1";
                            break;
                        default:
                            break;
                    }
                    attendanceFilters.push(filter);
                });

            setSeasonColumns(seasonColumns);
            setGameColumns(gameColumnsWithResponsive);
            setFilters(attendanceFilters);
        })()
    }, []);

    async function getData() {
        setSeasonData({ loading: true, rows: null });
        setGameData({ loading: true, rows: null });
        let filterObj = filters.reduce(addFilterAsParameter, { season: season });
        let resSeason = await GetReportAttendanceSeason(filterObj);
        let resGame = await GetReportAttendanceGame(filterObj);
        updateNotes(filters);
        if (resSeason.exceptionMsg) {
            setSeasonData({ loading: false, rows: null, exceptionMsg: resSeason.exceptionMsg });
        } else {
            setSeasonData({ loading: false, rows: resSeason });
        }
        if (resGame.exceptionMsg) {
            setGameData({ loading: false, rows: null, exceptionMsg: resGame.exceptionMsg });
        } else {
            setGameData({ loading: false, rows: resGame });
        }
    }

    function setFilter(filterIndex, type, value) {
        let newFilters = filters.slice();
        let filterToUpdate = newFilters.find(filter => filter.index === filterIndex)
        filterToUpdate[type] = value;
        setFilters(newFilters);
    }

    function updateNotes(filters) {
        let notes = {
            formNotes: [],
        };

        if (filters) {
            filters.forEach(filter => {
                if (filter.value) {
                    let filterNote = convertFilterToNote(filter);
                    if (filterNote)
                        notes.formNotes.push(filterNote);
                }
            });
        }

        setExcelNotes(notes);
    }

    let seasonOptions = [];
    for (let i = curSeason; i >= 2002; i--) {
        seasonOptions.push(
            <option key={i} value={i}>{i}</option>
        )
    };

    return (
        <div className='body attendance-report pdfSection'>
            <div className='pdfChunk'>
                <h2>Attendance Report</h2>
                <br />
                <Row>
                    <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>
                        Season:
                    </Form.Label>
                    <Form.Control
                        as='select'
                        defaultValue={season}
                        onChange={(event) => {
                            setSeason(event.target.value);
                        }}
                        style={{ width: '100px' }}
                    >
                        {seasonOptions}
                    </Form.Control>
                </Row>
                <br />
                <h5>What's on this report</h5>
                <div className='advanced-filter'>
                    {
                        filters.map((filter, filterIndex) =>
                            <StatFilter
                                filter={filter}
                                key={filterIndex}
                                people={filter.people}
                                setFilter={setFilter}
                            />
                        )
                    }
                </div>
                <br />
                <Row className='pdfIgnore'>
                    <Button variant='success' onClick={getData}>
                        Submit
                    </Button>
                    {
                        seasonData?.rows && seasonColumns && gameData?.rows && gameColumns && (
                        <div style={{ margin: '0px 0px 0px auto' }}>
                            <ExcelExporter
                                columns={{
                                    Season: seasonColumns,
                                    GameLogs: gameColumns,
                                }}
                                data={{
                                    Season: seasonData.rows,
                                    GameLogs: gameData.rows,
                                }}
                                notes={{
                                    Season: excelNotes,
                                    GameLogs: excelNotes,
                                }}
                                reportName={'Attendance'}
                                sheetNames={['Season', 'GameLogs']}
                            />
                            <PDFExporter fileName='AttendanceReport' />
                        </div>
                    )}
                </Row>
                <br />
            </div>
            {
                seasonData?.loading &&
                <div>
                    <br />
                    <br />
                    <i className='fa fa-spinner fa-spin loading-icon'></i>
                    <br />
                </div>
            }
            {
                seasonData?.rows && seasonColumns &&
                <div className='pdfChunk tableChunk'>
                    <Row>
                        <h3>Season - Total</h3>
                    </Row>
                    <div
                        style={{
                            width: (seasonColumns.reduce((acc, cur) => acc + ((cur.width) > 20 ? (cur.width) : 20), 0)) + 4
                        }}>
                        <TabulatorTable
                            cols={seasonColumns}
                            data={seasonData.rows}
                            initSort={[{
                                column: seasonColumns?.find(col => col.title === 'Team').field,
                                dir: 'asc'
                            }]}
                        />
                    </div>
                    <br />
                </div>
            }
            {
                seasonData?.exceptionMsg &&
                <React.Fragment>
                    <p style={{ color: 'rgb(220, 53, 69)', textAlign: 'center' }}>{seasonData.exceptionMsg}</p>
                    <br />
                    <br />
                </React.Fragment>
            }
            {
                gameData?.loading &&
                <div>
                    <br />
                    <br />
                    <i className='fa fa-spinner fa-spin loading-icon'></i>
                    <br />
                </div>
            }
            {
                gameData?.rows && gameColumns &&
                <div className='pdfChunk tableChunk'>
                    <Row>
                        <h3>Season - Game Logs</h3>
                    </Row>
                    <div
                        style={{
                            width: '1100px'
                        }}>
                        <TabulatorTable
                            cols={gameColumns}
                            data={gameData.rows}
                            initSort={[{
                                column: gameColumns?.find(col => col.title === 'Game Date').field,
                                dir: 'asc'
                            }]}
                            paginationSize={30}
                            responsiveLayout="collapse"
                        />
                    </div>
                    <br />
                </div>
            }
            {
                gameData?.exceptionMsg &&
                <React.Fragment>
                    <p style={{ color: 'rgb(220, 53, 69)', textAlign: 'center' }}>{gameData?.exceptionMsg}</p>
                    <br />
                    <br />
                </React.Fragment>
            }
        </div>
    )
}

export default AttendanceReport;